import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import srsService from '../services/srsService';
import questionHistoryService from '../services/questionHistoryService';
import sessionBuilderService from '../services/sessionBuilderService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import {
  PlayIcon,
  ClockIcon,
  AcademicCapIcon,
  FireIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// Lesson Node Component (moved top-level to avoid hook order issues)
const LessonNode = ({ node, onComplete }) => {
  const [hasRead, setHasRead] = useState(false);

  const renderWithLatex = (text) => {
    if (!text || typeof text !== 'string') {
      console.warn('renderWithLatex received non-string value:', text);
      return text || '';
    }
    const parts = text.split(/(\\\(.*?\\\)|\\\[.*?\\\])/);
    return parts.map((part, index) => {
      if (part.startsWith('\\(') && part.endsWith('\\)')) {
        const latex = part.slice(2, -2);
        try { return <InlineMath key={index} math={latex} />; } catch (error) { return <span key={index} className="text-red-500">{part}</span>; }
      }
      if (part.startsWith('\\[') && part.endsWith('\\]')) {
        const latex = part.slice(2, -2);
        try { return <BlockMath key={index} math={latex} />; } catch (error) { return <span key={index} className="text-red-500">{part}</span>; }
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleContinue = () => {
    setHasRead(true);
    onComplete({ type: 'lesson', hasRead: true });
  };

  const conceptTitle = node.conceptTitle || node.content?.conceptTitle || 'No title';
  const chapterTitle = node.chapterTitle || node.metadata?.chapterTitle || 'No chapter';
  const detailedExplanation = node.content?.detailedExplanation || 'No explanation available';

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{conceptTitle}</h2>
      <p className="text-gray-600 mb-6">{chapterTitle}</p>
      <div className="prose prose-lg prose-gray max-w-none mb-8">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={{
            // Custom components to handle LaTeX within markdown
            p: ({ children, ...props }) => {
              if (typeof children === 'string') {
                return <p {...props} className="mb-4 leading-relaxed">{renderWithLatex(children)}</p>;
              }
              return <p {...props} className="mb-4 leading-relaxed">{children}</p>;
            },
            strong: ({ children, ...props }) => {
              if (typeof children === 'string') {
                return <strong {...props} className="font-semibold text-gray-900">{renderWithLatex(children)}</strong>;
              }
              return <strong {...props} className="font-semibold text-gray-900">{children}</strong>;
            },
            em: ({ children, ...props }) => {
              if (typeof children === 'string') {
                return <em {...props} className="italic text-gray-800">{renderWithLatex(children)}</em>;
              }
              return <em {...props} className="italic text-gray-800">{children}</em>;
            },
            h1: ({ children, ...props }) => (
              <h1 {...props} className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b border-gray-200 pb-2">
                {children}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 {...props} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                {children}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 {...props} className="text-lg font-semibold text-gray-900 mt-5 mb-2">
                {children}
              </h3>
            ),
            ul: ({ children, ...props }) => (
              <ul {...props} className="list-disc list-inside space-y-2 mb-4 pl-4">
                {children}
              </ul>
            ),
            ol: ({ children, ...props }) => (
              <ol {...props} className="list-decimal list-inside space-y-2 mb-4 pl-4">
                {children}
              </ol>
            ),
            li: ({ children, ...props }) => {
              if (typeof children === 'string') {
                return <li {...props} className="mb-1 leading-relaxed">{renderWithLatex(children)}</li>;
              }
              return <li {...props} className="mb-1 leading-relaxed">{children}</li>;
            },
            blockquote: ({ children, ...props }) => (
              <blockquote {...props} className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 italic text-gray-700">
                {children}
              </blockquote>
            ),
            code: ({ children, ...props }) => (
              <code {...props} className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">
                {children}
              </code>
            ),
            pre: ({ children, ...props }) => (
              <pre {...props} className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4">
                {children}
              </pre>
            ),
          }}
        >
          {detailedExplanation}
        </ReactMarkdown>
      </div>
      <div className="flex justify-center">
        <button onClick={handleContinue} className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center">Got it — <span className="font-semibold ml-1">Start Practice</span><ArrowRightIcon className="h-4 w-4 ml-2" /></button>
      </div>
    </div>
  );
};

// Practice Node Component (moved top-level)
const PracticeNode = ({ node, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(new Date());
  const [questionsAnswered, setQuestionsAnswered] = useState([]);

  const questions = node.content.questions || [];

  useEffect(() => { setQuestionStartTime(new Date()); }, [currentQuestionIndex]);
  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswerSelect = (option) => { if (!isAnswered) setSelectedAnswer(option); };

  const renderWithLatex = (text) => {
    if (!text || typeof text !== 'string') { console.warn('renderWithLatex received non-string value:', text); return text || ''; }
    const parts = text.split(/(\\\(.*?\\\)|\\\[.*?\\\])/);
    return parts.map((part, index) => {
      if (part.startsWith('\\(') && part.endsWith('\\)')) { const latex = part.slice(2, -2); try { return <InlineMath key={index} math={latex} />; } catch (e) { return <span key={index} className="text-red-500">{part}</span>; } }
      if (part.startsWith('\\[') && part.endsWith('\\]')) { const latex = part.slice(2, -2); try { return <BlockMath key={index} math={latex} />; } catch (e) { return <span key={index} className="text-red-500">{part}</span>; } }
      return <span key={index}>{part}</span>;
    });
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer) return;
    const questionEndTime = new Date();
    const responseTime = (questionEndTime - questionStartTime) / 1000;
    const correctOption = currentQuestion.options.find(option => option.correct);
    const correct = selectedAnswer === correctOption?.id;
    setIsCorrect(correct);
    setIsAnswered(true);
    setShowExplanation(true);
    const questionResult = { questionId: currentQuestion.questionId, isCorrect: correct, responseTime, selectedAnswer, correctAnswer: correctOption?.id };
    setQuestionsAnswered(prev => [...prev, questionResult]);
    try { if (navigator.vibrate) navigator.vibrate(correct ? 20 : [30, 40, 30]); } catch {}
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setIsCorrect(false);
      setShowExplanation(false);
    } else {
      const score = questionsAnswered.filter(q => q.isCorrect).length / questions.length;
      onComplete({ score, errors: questionsAnswered.filter(q => !q.isCorrect).length, hintsUsed: 0, questionsAnswered });
    }
  };

  if (!currentQuestion) return <div>No questions available</div>;

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Practice Questions</h2>
        <p className="text-gray-600">Test your understanding</p>
      </div>
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Q{currentQuestionIndex + 1}/{questions.length}</span><span>{Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%</span></div>
        <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}></div></div>
      </div>
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children, ...props }) => {
                if (typeof children === 'string') {
                  return <span {...props} className="leading-relaxed">{renderWithLatex(children)}</span>;
                }
                return <span {...props} className="leading-relaxed">{children}</span>;
              },
              strong: ({ children, ...props }) => {
                if (typeof children === 'string') {
                  return <strong {...props} className="font-semibold">{renderWithLatex(children)}</strong>;
                }
                return <strong {...props} className="font-semibold">{children}</strong>;
              },
              em: ({ children, ...props }) => {
                if (typeof children === 'string') {
                  return <em {...props} className="italic">{renderWithLatex(children)}</em>;
                }
                return <em {...props} className="italic">{children}</em>;
              }
            }}
          >
            {currentQuestion.questionText}
          </ReactMarkdown>
        </h3>
        <div className="space-y-3 mb-6">
          {currentQuestion.options && currentQuestion.options.map((option, index) => (
            <button key={option.id || index} onClick={() => handleAnswerSelect(option.id)} disabled={isAnswered} className={`w-full p-4 text-left border-2 rounded-lg transition-all duration-200 ${selectedAnswer === option.id ? (isAnswered ? (option.correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50') : 'border-blue-500 bg-blue-50') : 'border-gray-300 hover:border-blue-300'}` }>
              <div className="flex items-center">
                <span className="font-medium mr-3 text-lg">{option.id})</span>
                                 <span className="text-gray-900">
                   <ReactMarkdown
                     remarkPlugins={[remarkGfm]}
                     components={{
                       p: ({ children, ...props }) => {
                         if (typeof children === 'string') {
                           return <span {...props} className="leading-relaxed">{renderWithLatex(children)}</span>;
                         }
                         return <span {...props} className="leading-relaxed">{children}</span>;
                       },
                       strong: ({ children, ...props }) => {
                         if (typeof children === 'string') {
                           return <strong {...props} className="font-semibold">{renderWithLatex(children)}</strong>;
                         }
                         return <strong {...props} className="font-semibold">{children}</strong>;
                       },
                       em: ({ children, ...props }) => {
                         if (typeof children === 'string') {
                           return <em {...props} className="italic">{renderWithLatex(children)}</em>;
                         }
                         return <em {...props} className="italic">{children}</em>;
                       }
                     }}
                   >
                     {option.text}
                   </ReactMarkdown>
                 </span>
                {isAnswered && option.correct && (<CheckCircleIcon className="h-5 w-5 text-green-600 ml-auto" />)}
                {isAnswered && option.id === selectedAnswer && !option.correct && (<XCircleIcon className="h-5 w-5 text-red-600 ml-auto" />)}
              </div>
            </button>
          ))}
        </div>
        {!isAnswered && (<div className="flex justify-center mb-6"><button onClick={handleSubmitAnswer} disabled={!selectedAnswer} className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Submit Answer</button></div>)}
                 {showExplanation && (
           <div className="mb-6 p-4 bg-gray-50 rounded-lg">
             <h3 className="font-semibold text-gray-900 mb-3">{isCorrect ? '✅ Correct!' : '❌ Try again'}</h3>
             <div className="prose prose-sm prose-gray max-w-none">
               <ReactMarkdown
                 remarkPlugins={[remarkGfm]}
                 rehypePlugins={[rehypeHighlight]}
                 components={{
                   p: ({ children, ...props }) => {
                     if (typeof children === 'string') {
                       return <p {...props} className="mb-3 leading-relaxed">{renderWithLatex(children)}</p>;
                     }
                     return <p {...props} className="mb-3 leading-relaxed">{children}</p>;
                   },
                   strong: ({ children, ...props }) => {
                     if (typeof children === 'string') {
                       return <strong {...props} className="font-semibold text-gray-900">{renderWithLatex(children)}</strong>;
                     }
                     return <strong {...props} className="font-semibold text-gray-900">{children}</strong>;
                   },
                   em: ({ children, ...props }) => {
                     if (typeof children === 'string') {
                       return <em {...props} className="italic text-gray-800">{renderWithLatex(children)}</em>;
                     }
                     return <em {...props} className="italic text-gray-800">{children}</em>;
                   },
                   ul: ({ children, ...props }) => (
                     <ul {...props} className="list-disc list-inside space-y-1 mb-3 pl-4">
                       {children}
                     </ul>
                   ),
                   ol: ({ children, ...props }) => (
                     <ol {...props} className="list-decimal list-inside space-y-1 mb-3 pl-4">
                       {children}
                     </ol>
                   ),
                   li: ({ children, ...props }) => {
                     if (typeof children === 'string') {
                       return <li {...props} className="mb-1 leading-relaxed">{renderWithLatex(children)}</li>;
                     }
                     return <li {...props} className="mb-1 leading-relaxed">{children}</li>;
                   },
                   code: ({ children, ...props }) => (
                     <code {...props} className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono text-gray-800">
                       {children}
                     </code>
                   ),
                 }}
               >
                 {currentQuestion.explanation}
               </ReactMarkdown>
             </div>
           </div>
         )}
        {isAnswered && (<div className="flex justify-center"><button onClick={handleNextQuestion} className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center">{currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Complete Practice'}<ArrowRightIcon className="h-4 w-4 ml-2" /></button></div>)}
      </div>
    </div>
  );
};

const ReviewNode = ({ node, onComplete }) => { return <PracticeNode node={node} onComplete={onComplete} />; };
const CheckpointNode = ({ node, onComplete }) => { return <PracticeNode node={node} onComplete={onComplete} />; };

const SessionInterface = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialSession = location.state?.session;
  const [currentSession, setCurrentSession] = useState(initialSession || null);
  
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [currentNode, setCurrentNode] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [nodeStartTime, setNodeStartTime] = useState(null);
  const [sessionResults, setSessionResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentSession && currentSession.nodes.length > 0) {
      setSessionStartTime(new Date());
      setCurrentNode(currentSession.nodes[0]);
      setNodeStartTime(new Date());
      setLoading(false);

      // Load resume state if available
      try {
        const resume = JSON.parse(localStorage.getItem(`resume_${user.uid}`) || 'null');
        if (resume && resume.sessionId === currentSession.sessionId) {
          // Basic restore: jump to saved question index if within practice node
          if (resume.nodeIndex && resume.nodeIndex < currentSession.nodes.length) {
            setCurrentNodeIndex(resume.nodeIndex);
            setCurrentNode(currentSession.nodes[resume.nodeIndex]);
          }
        }
      } catch {}
    }
  }, [currentSession]);

  const handleNodeComplete = async (result) => {
    const nodeEndTime = new Date();
    const nodeDuration = (nodeEndTime - nodeStartTime) / 1000; // in seconds
    
    const nodeResult = {
      nodeId: currentNode.nodeId,
      nodeType: currentNode.nodeType,
      subjectName: currentNode.subjectName,
      completed: true,
      score: result.score || 0,
      errors: result.errors || 0,
      hintsUsed: result.hintsUsed || 0,
      duration: nodeDuration,
      startTime: nodeStartTime,
      endTime: nodeEndTime,
      ...result
    };

    setSessionResults(prev => [...prev, nodeResult]);

    // Record question history for practice and review nodes
    if ((currentNode.nodeType === 'micro-practice' || currentNode.nodeType === 'review') && result.questionsAnswered) {
      try {
        for (const questionResult of result.questionsAnswered) {
          await questionHistoryService.recordQuestionAsked(
            user.uid,
            questionResult.questionId,
            {
              isCorrect: questionResult.isCorrect,
              responseTime: questionResult.responseTime
            }
          );
        }
      } catch (error) {
        console.error('Error recording question history:', error);
      }
    }

    // Update SRS if this is a review or practice node
    if (currentNode.nodeType === 'review' || currentNode.nodeType === 'micro-practice') {
      try {
        const conceptId = currentNode.metadata?.parentNodeId || currentNode.nodeId;
        const reviewResult = srsService.sm2plus({
          ease: 2.5,
          intervalDays: 0,
          reps: 0,
          latencyMs: nodeDuration * 1000,
          p80LatencyMs: 30000,
          correct: result.score >= 0.8,
          difficulty: 'medium'
        });

        await srsService.updateMastery(
          user.uid,
          conceptId,
          reviewResult,
          nodeDuration * 1000,
          result.score >= 0.8
        );
      } catch (error) {
        console.error('Error updating SRS:', error);
      }
    }

    // Move to next node or complete session
    if (currentNodeIndex < currentSession.nodes.length - 1) {
      const nextIndex = currentNodeIndex + 1;
      setCurrentNodeIndex(nextIndex);
      setCurrentNode(currentSession.nodes[nextIndex]);
      setNodeStartTime(new Date());

      // Persist lightweight resume info
      try {
        const totalQuestions = currentSession.totalQuestions || 0;
        const remainingSeconds = Math.max(0, (currentSession.estimatedDuration || 0) * 60 - (new Date() - sessionStartTime) / 1000);
        localStorage.setItem(
          `resume_${user.uid}`,
          JSON.stringify({
            sessionId: currentSession.sessionId,
            nodeIndex: nextIndex,
            questionIndex: 0,
            totalQuestions,
            remainingSeconds: Math.round(remainingSeconds)
          })
        );
      } catch {}
    } else {
      await completeSession();
    }
  };

  const completeSession = async () => {
    const sessionEndTime = new Date();
    const sessionDuration = (sessionEndTime - sessionStartTime) / 1000; // in seconds
    
    const sessionResult = {
      sessionId: currentSession.sessionId,
      userId: user.uid,
      completed: true,
      duration: sessionDuration,
      startTime: sessionStartTime,
      endTime: sessionEndTime,
      nodes: sessionResults,
      totalScore: sessionResults.length > 0 ? sessionResults.reduce((sum, result) => sum + (result.score || 0), 0) / sessionResults.length : 0
    };

    // Save session result to database and update user progress so we advance beyond the current lesson
    try {
      console.log('Session completed:', sessionResult);

      // Save session to Firestore (simple append)
      try {
        const { db } = await import('../firebase/config');
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        await addDoc(collection(db, 'sessions'), {
          ...sessionResult,
          createdAt: serverTimestamp()
        });
      } catch (e) {
        console.warn('Could not save session document (non-blocking):', e);
      }
      
      // Prepare normalized answers for progress update
      const answers = sessionResults
        .filter(r => (r.nodeType === 'micro-practice' || r.nodeType === 'review') && Array.isArray(r.questionsAnswered))
        .flatMap(r => r.questionsAnswered.map(a => ({
          questionId: a.questionId,
          isCorrect: !!a.isCorrect,
          responseTime: a.responseTime || 0,
          subjectName: r.subjectName
        })));

      const resultsForUpdate = {
        duration: sessionDuration,
        answers,
        nextNodeId: null, // let the service compute next based on completedNodes
        nextSubject: currentSession.nodes[0]?.subjectName || null
      };

      await sessionBuilderService.updateUserProgressAfterSession(user.uid, currentSession, resultsForUpdate);
      toast.success('Session completed! Loading your next session…');

      // Clear resume state on completion
      try { localStorage.removeItem(`resume_${user.uid}`); } catch {}

      // Build and start the next session automatically for continuous learning
      const updatedProgress = await sessionBuilderService.getUserProgress(user.uid);
      const nextSession = await sessionBuilderService.buildSession(user.uid, updatedProgress);

      if (nextSession && nextSession.nodes && nextSession.nodes.length > 0) {
        setCurrentSession(nextSession);
        setSessionResults([]);
        setCurrentNodeIndex(0);
        setCurrentNode(nextSession.nodes[0]);
        setSessionStartTime(new Date());
        setNodeStartTime(new Date());
        // stay on this page; continuous flow
        return;
      }
      // If nothing to study, go back to dashboard
      toast('All caught up for now!');
      navigate('/app');
    } catch (error) {
      console.error('Error saving session result:', error);
      toast.error('Error saving session result');
      // On failure, return to dashboard
      navigate('/app');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!currentSession || !currentNode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">No session data available</p>
          <button
            onClick={() => navigate('/app')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/app')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Learning Session</h1>
                <p className="text-sm text-gray-600">Node {currentNodeIndex + 1} of {currentSession.nodes.length}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-600"><ClockIcon className="h-4 w-4 mr-1" /><span>{Math.round((new Date() - sessionStartTime) / 1000 / 60)}m</span></div>
              {/* Timer toggle (placeholder) */}
              <button className="text-xs px-2 py-1 border rounded">Timer: Off</button>
              {/* Pause (saves resume) */}
              <button className="text-xs px-2 py-1 border rounded" onClick={()=>{
                try {
                  const remainingSeconds = Math.max(0, (currentSession.estimatedDuration || 0) * 60 - (new Date() - sessionStartTime) / 1000);
                  localStorage.setItem(`resume_${user.uid}`, JSON.stringify({ sessionId: currentSession.sessionId, nodeIndex: currentNodeIndex, questionIndex: 0, totalQuestions: currentSession.totalQuestions || 0, remainingSeconds: Math.round(remainingSeconds) }));
                } catch {}
                navigate('/dashboard');
              }}>Pause</button>
              {/* Report modal trigger (placeholder) */}
              <button className="text-xs px-2 py-1 border rounded" onClick={()=>alert('Report question: ambiguous/typo/wrong key')}>Report</button>
              <div className="flex items-center text-sm text-gray-600">
                <AcademicCapIcon className="h-4 w-4 mr-1" />
                <span>{currentSession.totalQuestions} questions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentNode.nodeType === 'lesson' && (
          <LessonNode node={currentNode} onComplete={handleNodeComplete} />
        )}
        {currentNode.nodeType === 'micro-practice' && (
          <PracticeNode node={currentNode} onComplete={handleNodeComplete} />
        )}
        {currentNode.nodeType === 'review' && (
          <ReviewNode node={currentNode} onComplete={handleNodeComplete} />
        )}
        {currentNode.nodeType === 'checkpoint' && (
          <CheckpointNode node={currentNode} onComplete={handleNodeComplete} />
        )}
      </div>
    </div>
  );
};
export default SessionInterface;
